import { zeroAddress } from 'viem'
import { toGenericPriceKey, toOracleKey } from '../../assets'
import { parseRawAmount } from '../../utils/parsing'
import { GenericTokenList } from '../../types'
import { composeVaultDisplayName } from '../displayName'
import { FluidFToken, FluidFTokens } from './types'

// Fluid uses the EEE sentinel (`0xEeee…EEeE`) on-chain for native ETH. Match
// the rest of the lending stack by normalizing to `zeroAddress` at the
// parser boundary.
const FLUID_EEE_LOWER = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'
const normalizeUnderlying = (addr: string): string =>
  addr === FLUID_EEE_LOWER ? zeroAddress : addr

/** Resolver rates use 1e2 precision (1 % = 100, 100 % = 10000). APR, not APY. */
const FLUID_RATE_SCALE = 1e2
const scaleFluidRate = (raw: string | number | bigint | undefined): number => {
  if (raw === undefined || raw === null) return 0
  return Number(raw) / FLUID_RATE_SCALE
}

/**
 * Parser for the single-call fToken fetch built by `buildFluidFTokensCall`.
 *
 *   data[0] = FTokenDetails[] (from LendingResolver.getFTokensEntireData)
 *
 * Returns a map keyed by lowercased underlying address. Native-ETH fTokens
 * key under `zeroAddress` to match the convention in `calldata-sdk`
 * (`isNativeAddress` / `normalizeNativeAddress`).
 */
export const getFluidFTokensConverter = (
  chainId: string,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
): [(data: any[]) => FluidFTokens | undefined, number] => {
  const expectedNumberOfCalls = 1

  return [
    (data: any[]) => {
      if (data.length !== expectedNumberOfCalls) return undefined
      const [fTokenDetails] = data
      return parseFTokens(fTokenDetails ?? [], chainId, prices, tokenList)
    },
    expectedNumberOfCalls,
  ]
}

function parseFTokens(
  fTokens: any[],
  chainId: string,
  prices: { [asset: string]: number },
  tokenList: GenericTokenList,
): FluidFTokens {
  const out: FluidFTokens = {}

  for (const ft of fTokens) {
    const rawAsset = (ft.asset ?? '').toString().toLowerCase()
    if (!rawAsset) continue
    const underlying = normalizeUnderlying(rawAsset)

    const assetMeta = tokenList[underlying]
    const decimals = Number(ft.decimals ?? assetMeta?.decimals ?? 18)

    const totalAssetsFormatted = Number(
      parseRawAmount(ft.totalAssets?.toString(), decimals),
    )

    const oracleKey =
      toOracleKey(assetMeta?.assetGroup) ?? toGenericPriceKey(underlying, chainId)
    const priceUsd = prices[oracleKey] ?? 0

    const supplyRate = scaleFluidRate(ft.supplyRate)
    const rewardsRate = scaleFluidRate(ft.rewardsRate)

    // `liquidityUserSupplyData.withdrawable` is the fToken's current
    // withdrawable allowance at the Liquidity Layer — the effective cap
    // for end-user exits right now (bounded by the shared-liquidity
    // withdrawal limit, not just the fToken's own supply).
    const liquidityRaw = ft.liquidityUserSupplyData?.withdrawable?.toString() ?? '0'
    const liquidityFormatted = Number(parseRawAmount(liquidityRaw, decimals))

    // Synthesize a clean `Fluid <symbol>` fallback when the resolver
    // returns an empty `name()` — integrators get a usable label without
    // having to special-case missing share-token names per chain.
    const onchainName = (ft.name ?? '').toString().trim()
    const underlyingSymbol = (assetMeta?.symbol ?? ft.symbol ?? '')
      .toString()
      .replace(/^f/, '') // strip leading `f` from share symbol fallback (`fUSDC` → `USDC`)
      .trim()
    const displayName = onchainName
      ? onchainName
      : underlyingSymbol
        ? `Fluid ${underlyingSymbol}`
        : 'Fluid'

    const entry: FluidFToken = {
      address: ft.tokenAddress,
      underlying,
      symbol: ft.symbol ?? '',
      name: displayName,
      displayName: composeVaultDisplayName(
        'Fluid',
        undefined,
        assetMeta,
        displayName,
      ),
      curatorName: 'Fluid',
      decimals,
      totalAssets: ft.totalAssets?.toString() ?? '0',
      totalSupply: ft.totalSupply?.toString() ?? '0',
      convertToShares: ft.convertToShares?.toString() ?? '0',
      convertToAssets: ft.convertToAssets?.toString() ?? '0',
      supplyRate,
      rewardsRate,
      depositRate: supplyRate + rewardsRate,
      isNativeUnderlying: !!ft.isNativeUnderlying,
      eip2612Deposits: !!ft.eip2612Deposits,
      asset: assetMeta,
      priceUsd: priceUsd || undefined,
      totalAssetsFormatted,
      totalAssetsUsd: totalAssetsFormatted * priceUsd,
      liquidity: liquidityRaw,
      liquidityFormatted,
      liquidityUsd: liquidityFormatted * priceUsd,
    }

    out[underlying] = entry
  }

  return out
}
